// =============================================================================
// admin-activation-alerts-client.ts — Client API de l'alerte 48h (§2.2 l.728), DOUBLABLE.
// =============================================================================
// Miroir de l'endpoint ADMIN read-only GET /admin/activation-alerts (BC identity,
// AdminSessionGuard). Comme les autres clients admin légers (operational-view…), expose une
// INTERFACE (AdminActivationAlertsClient) consommée par un hook TanStack ; l'impl HTTP réelle
// (fetch) vient derrière la MÊME interface sans toucher l'UI (DI seam, stub-first).
//
// DBU §2.2 l.728 : « Génération d'alertes automatiques sur le tableau de bord pour les comptes en
// attente d'activation au-delà de 48 heures. » L'AUTORITÉ est l'API : elle compte AU VOL les
// credentials « EnAttenteActivation » dont le dernier token a été émis il y a plus de `thresholdHours`.
// L'UI LIT le COMPTE scalaire (aucune PII : ni email, ni contact_id, ni token) et l'habille.
//
// DTO front LOCAL (PAS le Published Language, PAS @jeko/contracts) : donnée read-only intra-dashboard,
// non répliquée (gelé = 0). Route en GET (admin LIT, jamais d'écriture — invariant n°1).
//
//   GET /admin/activation-alerts → StalePendingActivationsDto

import { HttpApiError } from '@/api/http-error';

/**
 * Compte NON-nominatif des comptes en attente d'activation au-delà du seuil (§2.2 l.728). Miroir HTTP
 * du DTO back `{ stalePendingActivationCount, thresholdHours }`. Aucune donnée nominative : un scalaire.
 */
export interface StalePendingActivationsDto {
  /** Nombre de comptes « EnAttenteActivation » dont le dernier token date de plus de `thresholdHours`. */
  readonly stalePendingActivationCount: number;
  /** Seuil (heures) appliqué par l'API (défaut 48, §2.2 l.728). */
  readonly thresholdHours: number;
}

/**
 * §2.5 l.1085 (item 7, LOT-ALERTES, DEC-20260722-11) — UNE ligne NOMINATIVE du tableau « Comptes en
 * attente d'activation ». Miroir HTTP du DTO back. NOMINATIF ADMIS ADMIN-ONLY (AdminSessionGuard) :
 * `siret` (identifiant fournisseur) + `contactEmail` (contact) + dates du token ; JAMAIS hash/secret/IP.
 * `contactId` = clé opaque (React key). `tokenExpired` distingue « token EXPIRÉ » de « seulement > seuil ».
 */
export interface PendingActivationAccountDto {
  readonly contactId: string;
  readonly siret: string;
  readonly contactEmail: string;
  /** ISO 8601 — date d'émission du dernier token. */
  readonly issuedAt: string;
  /** ISO 8601 — date d'expiration du dernier token. */
  readonly expiresAt: string;
  readonly tokenExpired: boolean;
}

/**
 * §2.5 l.1085 (item 7) — TABLEAU nominatif des comptes en attente d'activation (critère : token EXPIRÉ
 * OU émis depuis > `thresholdHours`). `truncated` = liste bornée côté API ; le COUNT global reste porté
 * par `getStalePendingActivations()` (scalaire).
 */
export interface PendingActivationAccountsDto {
  readonly accounts: readonly PendingActivationAccountDto[];
  readonly thresholdHours: number;
  readonly truncated: boolean;
}

/**
 * Erreur applicative renvoyée par l'API admin activation-alerts (HTTP 4xx/5xx). L'UI l'affiche telle
 * quelle ; elle ne réinterprète JAMAIS la règle métier (autorité = API). Le 401 est traité comme une
 * erreur typée LOCALE (pas d'intercepteur global → landing : L-2 différé V1.5, DEC-20260721-6).
 */
export class AdminActivationAlertsApiError extends HttpApiError {
  public constructor(message: string, status?: number) {
    super(message, status, 'AdminActivationAlertsApiError');
  }
}

/**
 * Contrat du client read-only de l'alerte 48h (AdminSessionGuard). Aucune règle métier ici : le compte
 * est l'autorité de l'API (calcul AU VOL). L'UI relaie l'intention et habille.
 */
export interface AdminActivationAlertsClient {
  /** (§2.2 l.728) Compte des comptes en attente d'activation au-delà du seuil (défaut 48h). */
  getStalePendingActivations(): Promise<StalePendingActivationsDto>;

  /**
   * (§2.5 l.1085, item 7) TABLEAU NOMINATIF des comptes en attente d'activation (token EXPIRÉ OU émis
   * depuis > seuil). Vue ADMIN uniquement — l'autorité (RBAC, critère) est l'API ; l'UI LIT et habille.
   */
  getPendingActivationAccounts(): Promise<PendingActivationAccountsDto>;
}
